// frontend/src/modules/comment-monitor/PostCard.tsx
import React from 'react';
import type { PostWithCommentStats } from './useCommentMonitor.ts';

interface Props {
  post: PostWithCommentStats;
  onOpen: (id: string) => void;
  onSync: (id: string) => void;
  onToggleMonitor: (id: string, isMonitored: boolean) => void;
}

function truncate(s: string | null, len = 100): string {
  if (!s) return '';
  return s.length > len ? s.slice(0, len) + '…' : s;
}

export function PostCard({ post, onOpen, onSync, onToggleMonitor }: Props) {
  const date = post.createdTime
    ? new Intl.DateTimeFormat('uk-UA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(post.createdTime))
    : null;

  return (
    <div 
      className="glass-card hover:bg-slate-800/50 transition-colors" 
      style={{ 
        padding: 'var(--space-3) var(--space-4)', 
        display: 'flex', 
        alignItems: 'center',
        gap: 'var(--space-4)',
        cursor: 'pointer',
      }}
      onClick={() => onOpen(post.id)}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 'var(--radius-sm)',
          overflow: 'hidden',
          flexShrink: 0,
          background: 'var(--color-surface-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {(post.fullPicture || post.pictureUrl) ? (
          <img
            src={post.fullPicture ?? post.pictureUrl ?? ''}
            alt="post thumbnail"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <span className="text-subtle text-xs">No img</span>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: '4px' }}>
          <span className="font-medium" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>
            {post.pageName ?? post.pageId}
          </span>
          {post.groupName && (
            <span className="badge badge--currency" style={{ fontSize: '0.65rem' }}>
              {post.groupName}
            </span>
          )}
          {date && <span className="text-xs text-muted" style={{ marginLeft: 'var(--space-2)' }}>{date}</span>}
        </div>
        
        <p className="text-sm" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: 0 }}>
          {truncate(post.message, 120) || <span className="text-subtle">[без тексту]</span>}
        </p>
      </div>

      {/* Badges */}
      <div style={{ display: 'flex', gap: 'var(--space-2)', width: '220px', justifyContent: 'flex-end', flexWrap: 'wrap', flexShrink: 0 }}>
        {post.totalComments > 0 && (
          <span className="badge badge--active">
            {post.totalComments} ком.
          </span>
        )}
        {post.hiddenComments > 0 && (
          <span className="badge" style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-subtle)', border: '1px solid var(--color-border)' }}>
            {post.hiddenComments} прих.
          </span>
        )}
        {post.unreadComments > 0 && (
          <span 
            className="badge" 
            style={{ 
              background: 'var(--color-primary)', 
              color: 'white', 
              animation: 'pulse 2s infinite' 
            }}
          >
            {post.unreadComments} нові
          </span>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0, alignItems: 'center' }}>
        <button 
          className="btn" 
          style={{ 
            padding: '0.25rem 0.5rem', 
            fontSize: '0.75rem', 
            height: '30px', 
            background: post.isMonitored ? 'var(--color-primary)' : 'transparent',
            color: post.isMonitored ? 'white' : 'var(--color-text-subtle)',
            border: `1px solid ${post.isMonitored ? 'var(--color-primary)' : 'var(--color-border)'}`
          }}
          onClick={(e) => { e.stopPropagation(); onToggleMonitor(post.id, !post.isMonitored); }}
          title={post.isMonitored ? "Вимкнути моніторинг" : "Увімкнути моніторинг"}
        >
          {post.isMonitored ? "On" : "Off"}
        </button>
        <button 
          className="btn btn--outline" 
          style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', height: '30px' }}
          onClick={(e) => { e.stopPropagation(); onSync(post.id); }}
        >
          Оновити
        </button>
        <a 
          href={post.permalinkUrl ?? '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn--secondary"
          style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', textDecoration: 'none', height: '30px', display: 'flex', alignItems: 'center' }}
          onClick={(e) => e.stopPropagation()}
        >
          FB ↗
        </a>
      </div>
    </div>
  );
}
